import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

/**
 * SEO Component for managing page meta tags with multilingual support
 * @param {string} title - Page title (optional, uses translation if not provided)
 * @param {string} description - Page meta description (optional, uses translation if not provided)
 * @param {string} image - Open Graph image URL
 * @param {string} url - Canonical URL (optional, uses current path)
 * @param {string} type - Open Graph type (website, article, etc.)
 * @param {string} translationKey - Key for translations (e.g., "seo.home")
 */
function SEO({
  title,
  description,
  image = "/assets/img/logo-haseki.png",
  url,
  type = "website",
  translationKey,
}) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language || 'tr';

  // Get translations if translationKey is provided
  const seoTitle = title || (translationKey ? t(`${translationKey}.title`) : t("seo.home.title"));
  const seoDescription = description || (translationKey ? t(`${translationKey}.description`) : t("seo.home.description"));

  // Default site URL (update this with your actual domain)
  const siteUrl = process.env.REACT_APP_SITE_URL || "https://hasekisultankizyurdu.com";
  
  // Build URL with language prefix
  const currentPath = url || location.pathname;
  const pathWithoutLang = currentPath.replace(/^\/(tr|en)/, '') || '/';
  const langPrefix = currentLang === 'tr' ? '' : `/${currentLang}`;
  const fullUrl = `${siteUrl}${langPrefix}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
  
  // Alternate URLs for hreflang
  const trUrl = `${siteUrl}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
  const enUrl = `${siteUrl}/en${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
  
  const fullImageUrl = image.startsWith("http") ? image : `${siteUrl}${image}`;

  const languageMap = {
    tr: "Turkish",
    en: "English"
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={languageMap[currentLang] || "Turkish"} />
      <meta name="author" content="Haseki Sultan Kız Yurdu" />

      {/* Language Alternates for SEO */}
      <link rel="alternate" hreflang="tr" href={trUrl} />
      <link rel="alternate" hreflang="en" href={enUrl} />
      <link rel="alternate" hreflang="x-default" href={trUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Haseki Sultan Kız Öğrenci Yurdu" />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLang === 'tr' ? 'en_US' : 'tr_TR'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
}

export default SEO;
